// utils/helpers.js
// Helper utility functions extracted from Profile.js

export const safeParseJson = data => {
  if (typeof data !== "string") return null;
  try {
    return JSON.parse(data);
  } catch (e) {
    console.error("Failed to parse JSON data:", e, "Data:", data);
    return null;
  }
};

export const retry = async (fn, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i === retries - 1) throw err;
      console.warn(`Retry ${i + 1}/${retries} failed: ${err.message}`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Helper function to convert daily data to cumulative
const makeCumulative = (arr) => {
  const cumulative = [...arr];
  for (let i = 1; i < cumulative.length; i++) {
    cumulative[i] += cumulative[i - 1];
  }
  return cumulative;
};

// Generate time-series data for charts from user history
export const generateTimeSeriesData = (history, streaks, pointsHistory) => {
  const days = 30;
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  
  // Initialize arrays
  const githubData = Array(days).fill(0);
  const xData = Array(days).fill(0);
  const amPointsData = Array(days).fill(0);
  const gmData = Array(days).fill(0);
  const gnData = Array(days).fill(0);
  const senditData = Array(days).fill(0);

  // Process history entries
  if (history && history.length > 0) {
    history.forEach(entry => {
      const entryDate = new Date(entry.timestamp * 1000);
      const daysDiff = Math.floor((today.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24));
      const dayIndex = days - 1 - daysDiff;
      
      if (dayIndex >= 0 && dayIndex < days) {
        const points = entry.pointsEarned || 1;
        
        switch(entry.activityType) {
          case "githubInteraction":
          case "github":
            githubData[dayIndex] += points;
            break;
          case "xInteraction":
          case "tweet":
          case "x":
            xData[dayIndex] += points;
            break;
          case "AIActivity":
          case "ai":
            amPointsData[dayIndex] += points;
            break;
          case "gm":
            gmData[dayIndex] += points;
            break;
          case "gn":
            gnData[dayIndex] += points;
            break;
          case "sendit":
            senditData[dayIndex] += points;
            break;
        }
      }
    });
  }

  // Process streaks if available
  if (streaks) {
    ['gm', 'gn', 'sendit'].forEach(streakType => {
      if (streaks[streakType]?.history) {
        streaks[streakType].history.forEach(ts => {
          const date = new Date(ts * 1000);
          const daysDiff = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
          const dayIndex = days - 1 - daysDiff;
          
          if (dayIndex >= 0 && dayIndex < days) {
            if (streakType === 'gm') gmData[dayIndex] = 1;
            else if (streakType === 'gn') gnData[dayIndex] = 1;
            else if (streakType === 'sendit') senditData[dayIndex] = 1;
          }
        });
      }
    });
  }

  // Create cumulative growth for GitHub and X
  const githubGrowth = makeCumulative(githubData);
  const xGrowth = makeCumulative(xData);

  // Create cumulative AmPoints growth if pointsHistory is available
  let cumulativePoints = Array(days).fill(0);
  if (pointsHistory && pointsHistory.length > 0) {
    // Sort by timestamp
    const sortedHistory = [...pointsHistory].sort((a, b) => a.timestamp - b.timestamp);
    
    let runningTotal = 0;
    for (let i = 0; i < days; i++) {
      const currentDate = new Date(today);
      currentDate.setDate(currentDate.getDate() - (days - 1 - i));
      currentDate.setHours(0, 0, 0, 0);
      
      // Add all points earned up to this day
      sortedHistory.forEach(entry => {
        const entryDate = new Date(entry.timestamp * 1000);
        if (entryDate <= currentDate) {
          runningTotal += (entry.pointsEarned || 0);
        }
      });
      
      cumulativePoints[i] = runningTotal;
      runningTotal = 0; // Reset for next iteration
    }
  } else {
    // If no pointsHistory, calculate cumulative from all daily data
    const totalDailyPoints = Array(days).fill(0);
    for (let i = 0; i < days; i++) {
      totalDailyPoints[i] = githubData[i] + xData[i] + amPointsData[i] + gmData[i] + gnData[i] + senditData[i];
    }
    cumulativePoints = makeCumulative(totalDailyPoints);
  }

  return { 
    github: githubGrowth,  // Now returns cumulative growth
    x: xGrowth,           // Now returns cumulative growth
    amPoints: cumulativePoints,
    gm: gmData,           // Keeping as daily data (used by other parts)
    gn: gnData,           // Keeping as daily data (used by other parts)
    sendit: senditData    // Keeping as daily data (used by other parts)
  };
};